import { Button, Modal, PageHeader, DatePicker, Form } from 'antd';
import MapCard from 'app/containers/Map/parts/MapCard/MapCard';
import { MapModeEnum } from 'app/containers/Map/slice/types';
import { RouteConstants } from 'app/routes';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { generatePath, RouteComponentProps } from 'react-router';
import { MenuInfo } from 'types';
import { LEFT_COL_WIDTH } from 'utils/constants';
import TaskTabs from '../../parts/TaskTabs/TaskTabs';
import { useTaskSlice } from '../../slice';
import {
  selectTaskProgressState,
  selectTaskResourcesState,
} from '../../slice/selectors';
import {
  detailsViewTypeEnum,
  IFieldProgress,
  IFieldTaskProgressUpdateViewsListItem,
} from '../../slice/types';

import * as S from './TaskProgressUpdate.styles';
import { SelectedFieldsSummary } from '../../parts/SelectedFieldsSummary/SelectedFieldsSummary';
import {
  selectFieldsProgress,
  selectInitialFieldsProgress,
} from 'app/containers/Map/slice/selectors';
import { useMapSlice } from 'app/containers/Map/slice';
import { selectSelectedEnvId } from 'app/containers/HiikoEnv/slice/selectors';
import { selectSelectedSeasonId } from 'app/containers/Season/slice/selectors';
import {
  selectSelectedCompanyGroupId,
  selectSelectedCompanyId,
} from 'app/containers/Company/slice/selectors';
import TaskProgressResourcesCard from '../../parts/TaskProgressResourcesCard/TaskProgressResourcesCard';
import { QuestionCircleOutlined } from '@ant-design/icons';
import moment, { Moment } from 'moment';
import { checkIsNotNegativeNumber } from 'utils/validationHelpers';
import NumberInput from 'app/components/NumberInput/NumberInput';
import ConfigProviderWrapper from 'app/containers/ConfigProviderWrapper/ConfigProviderWrapper';
import TaskBasicInfoCard from '../../parts/TaskBasicInfoCard/TaskBasicInfoCard';

interface IReactRouterParams {
  taskId: string;
}

interface IProps extends RouteComponentProps<IReactRouterParams> {}

const TaskProgressUpdate = ({
  history,
  location,
  match: {
    params: { taskId },
  },
}: IProps) => {
  const { t } = useTranslation();

  const { actions } = useTaskSlice();
  const { actions: mapActions } = useMapSlice();

  const dispatch = useDispatch();

  const [form] = Form.useForm();

  const fieldTaskProgressUpdate = useSelector(selectFieldsProgress);
  const { loading, task, notes } = useSelector(selectTaskProgressState);
  const envId = useSelector(selectSelectedEnvId);
  const seasonId = useSelector(selectSelectedSeasonId);
  const companyId = useSelector(selectSelectedCompanyId);
  const companyGroupId = useSelector(selectSelectedCompanyGroupId);
  const {
    plannedResources: { updatedResourcesArr: plannedResourcesArr },
  } = useSelector(selectTaskResourcesState);
  const initialFieldsProgress = useSelector(selectInitialFieldsProgress);
  console.log('fieldTaskProgressUpdate:', fieldTaskProgressUpdate);
  const [selectedKey, setSelectedKey] = useState(detailsViewTypeEnum.map);

  const onClickMenu = (clickedItem: MenuInfo) => {
    const key = clickedItem.key as detailsViewTypeEnum;
    setSelectedKey(key);
  };

  useEffect(() => {
    if (taskId && envId && seasonId) {
      dispatch(
        actions.getTaskProgress({
          envId,
          taskId: Number(taskId),
        }),
      );
      dispatch(
        mapActions.onLoad({ envId, seasonId, companyId, companyGroupId }),
      );
      dispatch(
        actions.getTaskPlannedResources({ envId, taskId: Number(taskId) }),
      );
      dispatch(actions.getTaskUsedResources({ envId, taskId: Number(taskId) }));
      dispatch(actions.getAllAvailableTaskResources(envId));
    }
  }, [
    taskId,
    actions,
    dispatch,
    envId,
    mapActions,
    seasonId,
    companyId,
    companyGroupId,
  ]);

  // Set prefilled this stage used resources from planned resources data
  useEffect(() => {
    if (plannedResourcesArr) {
      dispatch(actions.setTaskThisStageUsedResources(plannedResourcesArr));
    }
  }, [plannedResourcesArr, dispatch, actions]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const viewTypeInUrl = query.get('tab') as detailsViewTypeEnum;
    if (
      viewTypeInUrl !== selectedKey &&
      Object.values(detailsViewTypeEnum).includes(viewTypeInUrl)
    ) {
      setSelectedKey(viewTypeInUrl as detailsViewTypeEnum);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(location.search);

    const tabQueryParam = query.get('tab') || undefined;

    if (selectedKey && selectedKey !== tabQueryParam) {
      query.set('tab', selectedKey);
    }
    history.push({ search: query.toString() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKey, location.search]);

  useEffect(() => {
    const query = new URLSearchParams(location.search);
    const isAllMarked = query.get('all-fields-done')?.toLowerCase() === 'true';
    if (initialFieldsProgress && isAllMarked) {
      const updatedFields = initialFieldsProgress.map(field => ({
        ...field,
        progress: 100,
      }));
      dispatch(mapActions.setUpdatedFieldsProgressArr(updatedFields));
    }
  }, [initialFieldsProgress, mapActions, dispatch, location]);

  const getTotalUpdatedAreaAndUpdatedFieldsAreas = useCallback(
    ({
      initialFields,
      updatedFields,
    }: {
      initialFields: IFieldProgress[];
      updatedFields: IFieldProgress[];
    }) => {
      return initialFields.reduce(
        (
          accumulator: {
            fieldsUpdatedAreas: { [key: number]: number };
            totalUpdatedArea: number;
            isUpdatedArea: boolean;
          },
          initialField,
        ) => {
          const updatedField = updatedFields.find(
            field => field.fieldTaskId === initialField.fieldTaskId,
          );

          if (updatedField && updatedField.progress > initialField.progress) {
            accumulator.isUpdatedArea = true;

            const updatedFieldArea =
              ((updatedField.progress - initialField.progress) / 100) *
              initialField.area;

            accumulator.fieldsUpdatedAreas[
              initialField.fieldTaskId
            ] = updatedFieldArea;

            accumulator.totalUpdatedArea =
              accumulator.totalUpdatedArea + updatedFieldArea;
          }
          return accumulator;
        },
        { fieldsUpdatedAreas: {}, totalUpdatedArea: 0, isUpdatedArea: false },
      );
    },
    [],
  );

  const saveProgressData = useCallback(
    ({
      dateRange,
      harvest,
    }: {
      dateRange: Moment[];
      harvest?: number | string;
    }) => {
      const startDate = dateRange[0].format('YYYY-MM-DD');
      const endDate = dateRange[1].format('YYYY-MM-DD');
      if (envId && fieldTaskProgressUpdate && initialFieldsProgress) {
        const totalHarvest = Number(harvest);

        const {
          fieldsUpdatedAreas,
          totalUpdatedArea,
        } = getTotalUpdatedAreaAndUpdatedFieldsAreas({
          initialFields: initialFieldsProgress,
          updatedFields: fieldTaskProgressUpdate,
        });

        const fields: IFieldTaskProgressUpdateViewsListItem[] = fieldTaskProgressUpdate.map(
          ({ fieldTaskId, progress }) => {
            const harvestResult =
              (totalHarvest / totalUpdatedArea) *
                (fieldsUpdatedAreas[fieldTaskId] || 0) || 0;

            return {
              fieldTaskId,
              progress,
              startDate,
              endDate,
              harvest: harvestResult,
            };
          },
        );

        dispatch(
          actions.updateTaskProgress({
            envId,
            fieldTaskProgressUpdateViews: fields,
            taskId: Number(taskId),
          }),
        );
      }
    },
    [
      fieldTaskProgressUpdate,
      taskId,
      actions,
      dispatch,
      envId,
      initialFieldsProgress,
      getTotalUpdatedAreaAndUpdatedFieldsAreas,
    ],
  );

  const openDateModal = useCallback(() => {
    Modal.confirm({
      style: { width: 425 },
      title: task?.category.producesYield
        ? t(
            'Field.ProgressUpdate.DateModal.ChooseDateAndHarvest',
            'Select the date when the task was completed and fill collected harvest field.',
          )
        : t(
            'Field.ProgressUpdate.DateModal.ChooseDate',
            'Select the date when the task was completed.',
          ),
      icon: <QuestionCircleOutlined />,
      content: (
        <ConfigProviderWrapper>
          <Form
            form={form}
            id="progressDateForm"
            onFinish={saveProgressData}
            layout="vertical"
            requiredMark={false}
          >
            <Form.Item
              name="dateRange"
              label={t(
                'Field.ProgressUpdate.DateModal.ChooseDate',
                'Select the date when the task was completed.',
              )}
              rules={[{ required: true }]}
            >
              <DatePicker.RangePicker style={{ width: '100%' }} />
            </Form.Item>
            {task?.category.producesYield && (
              <Form.Item
                name="harvest"
                label={'Koks buvo nuimto derliaus kiekis (t) iš viso?'}
                rules={[
                  { required: true },
                  { validator: checkIsNotNegativeNumber },
                ]}
              >
                <NumberInput placeholder="Įveskite kiekį tonomis" />
              </Form.Item>
            )}
          </Form>
        </ConfigProviderWrapper>
      ),
      okButtonProps: {
        form: 'progressDateForm',
        htmlType: 'submit',
      },

      okText: t('Field.ProgressUpdate.DateModal.Confirm', 'Confirm'),
      cancelText: t('Field.ProgressUpdate.DateModal.Cancel', 'Cancel'),
      onOk() {
        return new Promise<void>((resolve, reject) => {
          form
            .validateFields()
            .then(() => resolve())
            .catch(() => reject());
        });
      },
    });
  }, [t, form, saveProgressData, task]);

  const isTaskIsDone = !initialFieldsProgress?.some(
    fieldProgress => fieldProgress.progress !== 100,
  );

  const headerButtonsArr = [
    <Button
      key="1"
      type="primary"
      onClick={() => {
        if (initialFieldsProgress && fieldTaskProgressUpdate) {
          const { isUpdatedArea } = getTotalUpdatedAreaAndUpdatedFieldsAreas({
            initialFields: initialFieldsProgress,
            updatedFields: fieldTaskProgressUpdate,
          });
          // If no updated area - it is just progress correction and we don't need harvest and date
          if (isUpdatedArea) {
            openDateModal();
          } else {
            saveProgressData({ dateRange: [moment(), moment()], harvest: 0 });
          }
        }
      }}
    >
      {t('Task.ProgressUpdate.Save', 'Save')}
    </Button>,
  ];

  if (!envId || loading) {
    return null;
  }

  return (
    <>
      <PageHeader
        onBack={() =>
          history.push(
            generatePath(RouteConstants.task.details, {
              envId: envId,
              taskId: Number(taskId),
            }),
          )
        }
        className="page-header"
        title={task?.name}
        extra={headerButtonsArr}
      />
      <S.Container wrap={false}>
        <S.LeftCol flex={LEFT_COL_WIDTH}>
          <TaskTabs
            onClickMenu={onClickMenu}
            selectedKey={selectedKey}
            isResourcesTabHidden={isTaskIsDone}
          />
          <TaskBasicInfoCard
            categoryName={task?.category.name}
            endDate={task?.endDate}
            startDate={task?.startDate}
            notes={notes}
          />

          <SelectedFieldsSummary
            fieldTasks={task?.fieldTasks}
            fieldTaskProgressUpdate={fieldTaskProgressUpdate}
          />
        </S.LeftCol>

        {selectedKey === detailsViewTypeEnum.map ? (
          <S.MapCol flex="auto">
            <MapCard
              mode={MapModeEnum.progress}
              title={t('Task.ProgressUpdate.MapTitle', 'Task fields')}
            />
            <S.MessageCard size="small" bordered={false}>
              <S.CheckIcon />
              {isTaskIsDone
                ? t(
                    'Task.ProgressUpdate.ChangeFieldProgressToChangeTaskStatus',
                    'If you want to change the status of the task, change the completion of at least 1 field.',
                  )
                : t(
                    'Task.ProgressUpdate.SelectFieldsMsg',
                    'Select the fields in which you performed the work.',
                  )}
            </S.MessageCard>
          </S.MapCol>
        ) : (
          <S.ResourcesCol flex="auto">
            <TaskProgressResourcesCard />
          </S.ResourcesCol>
        )}
      </S.Container>
    </>
  );
};

export default TaskProgressUpdate;
